import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../../../../common/types';
import { WidgetEditionProps } from '../../props';
import { FieldsType } from '../../../../wizards/presaveLink/consts';
import { usePresaveLinkForm } from '../../../../wizards/presaveLink/usePresaveLinkForm';
import { PresaveServicesField } from '../../../../wizards/presaveLink/PresaveServicesField';
import { useCreatePresaveCampaignForSmartLinkMutation } from '../../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../../redux/api/utils/isSuccessfulResult';
import { PresaveWidgetFormContainer } from './PresaveWidgetFormContainer';

type PresaveWidgetCreateFormProps = Pick<
  WidgetEditionProps<SerializedPresaveWidget>,
  'accountId' | 'smartLinkId' | 'saveWidget' | 'isLoading' | 'error'
>;

/**
 * Adding a pre-save block to a page that already exists.
 *
 * Two calls, in this order: the campaign is written first, and the block is
 * created with the uuid it returns. The other order is impossible — a block
 * with no campaign behind it has nothing to draw.
 */
const PresaveWidgetCreateFormComponent: React.FC<PresaveWidgetCreateFormProps> = ({
  accountId,
  smartLinkId,
  saveWidget,
  isLoading,
  error,
}) => {
  const intl = useIntl();
  const [services, setServices] = useState<string[]>(defaultPresaveServices);
  const [createCampaign, { isLoading: isCreatingCampaign, error: createCampaignError }] =
    useCreatePresaveCampaignForSmartLinkMutation();

  const onSuccess = useCallback(
    (values: FieldsType) => {
      const run = async (): Promise<void> => {
        const result = await createCampaign({
          accountId,
          smartLinkId,
          title: values.title,
          artistName: values.artistName,
          releaseDate: values.releaseDate,
        });
        // A campaign already on this page, or a date already gone. The server
        // says which; the container shows it and the block is not created.
        if (!isSuccessfulResult(result)) {
          return;
        }
        await saveWidget({ campaignUuid: result.data.campaign.uuid, services }, SmartLinkWidgetType.presave);
      };
      run().catch(console.error);
    },
    [accountId, createCampaign, saveWidget, services, smartLinkId]
  );

  const busy = isLoading || isCreatingCampaign;
  const { submit, TitleInput, ArtistNameInput, ReleaseDateInput } = usePresaveLinkForm({
    isLoading: busy,
    onSuccess,
  });

  return (
    <PresaveWidgetFormContainer
      accountId={accountId}
      smartLinkId={smartLinkId}
      isLoading={busy}
      error={error ?? createCampaignError}
      onSubmit={submit}
      // A block with no buttons on it is not a pre-save block. The server
      // rejects an empty list too; this says so before the round trip.
      submitDisabled={services.length === 0}
    >
      {TitleInput}
      {ArtistNameInput}
      {ReleaseDateInput}
      <PresaveServicesField
        selected={services}
        onChange={setServices}
        disabled={busy}
        label={intl.formatMessage({ id: 'app.sml.presave.form.services', defaultMessage: 'Pre-save on' })}
        help={intl.formatMessage({
          id: 'app.sml.presave.form.services_help',
          defaultMessage: 'Fans on these services save the track the moment you release it. Pick at least one.',
        })}
      />
    </PresaveWidgetFormContainer>
  );
};

export const PresaveWidgetCreateForm = TypedMemo(PresaveWidgetCreateFormComponent);
